import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { bulkCheckProcessed, useBulkCheckStatus } from "@/lib/accounts";
import { ProgressBar } from "@/components/ui/progress";
import { pushToast } from "@/components/Toaster";

/**
 * How far a bulk "Проверить" is. The checks run in the background, minutes
 * apart, so without this line the page gives no sign that anything is going
 * on. Shown only while a batch runs; says how it ended when it does.
 */
export function BulkCheckProgress() {
  const { t } = useTranslation();
  const { data } = useBulkCheckStatus();
  const wasActive = useRef(false);

  useEffect(() => {
    if (!data) return;
    // `total` is 0 once the record itself has expired (the worker never got to
    // the batch) — that is not a finished check, and there is nothing to report.
    if (wasActive.current && !data.active && data.total > 0) {
      const parts = [t("accounts.bulk_check.finished", { done: data.done })];
      if (data.failed > 0) {
        parts.push(t("accounts.bulk_check.finished_failed", { failed: data.failed }));
      }
      if (data.skipped > 0) {
        parts.push(t("accounts.bulk_check.finished_skipped", { skipped: data.skipped }));
      }
      const clean = parts.length === 1;
      pushToast({
        kind: clean ? "success" : "warning",
        text: parts.join(" "),
        durationMs: clean ? 5000 : 12000,
      });
    }
    wasActive.current = data.active;
  }, [data, t]);

  if (!data?.active) return null;

  return (
    <div className="rounded-xl border border-border bg-bg-secondary p-3">
      <ProgressBar
        value={bulkCheckProcessed(data)}
        max={data.total}
        showValue
        label={t("accounts.bulk_check.progress")}
      />
      <p className="mt-1.5 text-xs text-fg-muted">{t("accounts.bulk_check.progress_hint")}</p>
    </div>
  );
}
BulkCheckProgress.displayName = "BulkCheckProgress";
